import { core } from './client';
import {
  CANONICAL_CONCEPTS,
  TERMINOLOGY_SCOPES,
  type CanonicalConcept,
  type CapabilityPackDefinition,
  type ResolvedCapabilitySet,
  type ResolvedPack,
  type ResolvedTerm,
  type ResolvedTerminology,
  type TermSource,
  type TerminologyOverride,
  type TerminologyScope,
  type WriteTerminologyLabel,
} from '@/types/institution-experience';

/**
 * The institution-experience endpoints on campusone-core-service.
 *
 * CORE routes, so everything goes through the core BFF helper — the same proxy and
 * envelope-stripping as api.admin.*. Not the identity helper: identity knows nothing
 * about terminology or packs, and pointing there returns a 404 that reads like the
 * feature never shipped.
 *
 * Writes are guarded server-side by ExperienceAdminOnly (platform admins and
 * institution/portal administrators) plus a token-claim check that the caller's own
 * institution matches the URL. A 403 from these routes is therefore a statement
 * about the caller, and its message — "You may only administer your own
 * institution" — is rendered verbatim.
 *
 * Core validates with forbidNonWhitelisted, so every body below carries the DTO's
 * fields and nothing else; an extra key is a 400, not a shrug.
 */

const seg = encodeURIComponent;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

function isCanonicalConcept(value: unknown): value is CanonicalConcept {
  return typeof value === 'string' && (CANONICAL_CONCEPTS as readonly string[]).includes(value);
}

function isTerminologyScope(value: unknown): value is TerminologyScope {
  return typeof value === 'string' && (TERMINOLOGY_SCOPES as readonly string[]).includes(value);
}

/* ─── Terminology normalisers ─── */

function normalizeResolvedTerm(key: CanonicalConcept, value: unknown): ResolvedTerm | null {
  if (!isRecord(value)) return null;
  const singular = typeof value.singular === 'string' ? value.singular : '';
  const plural = typeof value.plural === 'string' ? value.plural : singular;
  // A term with no singular is unusable: the service never sends one, and rendering
  // an empty cell as if it were a word would look like data loss to the reader.
  if (!singular) return null;
  const source: TermSource =
    isTerminologyScope(value.source) || value.source === 'PLATFORM_DEFAULT'
      ? (value.source as TermSource)
      : 'PLATFORM_DEFAULT';
  return {
    singular,
    plural,
    short: typeof value.short === 'string' && value.short ? value.short : singular,
    canonicalKey: key,
    source,
    locale: typeof value.locale === 'string' ? value.locale : '',
  };
}

function normalizeResolvedTerminology(payload: unknown): ResolvedTerminology {
  if (!isRecord(payload) || !isRecord(payload.terms)) {
    throw new Error('The terminology response was not in the expected shape.');
  }
  const terms: ResolvedTerminology['terms'] = {};
  for (const key of CANONICAL_CONCEPTS) {
    const term = normalizeResolvedTerm(key, payload.terms[key]);
    if (term) terms[key] = term;
  }
  const layers = Array.isArray(payload.layers)
    ? payload.layers.flatMap((layer) =>
        isRecord(layer) && isTerminologyScope(layer.scope)
          ? [{ scope: layer.scope, scopeRef: typeof layer.scopeRef === 'string' ? layer.scopeRef : null }]
          : [],
      )
    : [];
  return {
    institutionId: typeof payload.institutionId === 'string' ? payload.institutionId : '',
    campusId: typeof payload.campusId === 'string' ? payload.campusId : null,
    locale: typeof payload.locale === 'string' ? payload.locale : 'en',
    layers,
    terms,
  };
}

function normalizeOverride(value: unknown): TerminologyOverride | null {
  if (!isRecord(value)) return null;
  if (!isCanonicalConcept(value.canonicalKey) || !isTerminologyScope(value.scope)) return null;
  if (typeof value.singularLabel !== 'string' || typeof value.locale !== 'string') return null;
  return {
    id: typeof value.id === 'string' ? value.id : `${value.canonicalKey}-${value.locale}`,
    canonicalKey: value.canonicalKey,
    scope: value.scope,
    scopeRef: typeof value.scopeRef === 'string' ? value.scopeRef : null,
    locale: value.locale,
    singularLabel: value.singularLabel,
    pluralLabel: typeof value.pluralLabel === 'string' ? value.pluralLabel : value.singularLabel,
    shortLabel: typeof value.shortLabel === 'string' ? value.shortLabel : null,
    updatedBy: typeof value.updatedBy === 'string' ? value.updatedBy : null,
  };
}

function normalizeOverrides(payload: unknown): TerminologyOverride[] {
  if (!Array.isArray(payload)) return [];
  return payload.flatMap((row) => {
    const override = normalizeOverride(row);
    return override ? [override] : [];
  });
}

/* ─── Capability pack normalisers ─── */

function normalizePackDefinition(value: unknown): CapabilityPackDefinition | null {
  if (!isRecord(value)) return null;
  if (typeof value.pack !== 'string' || typeof value.title !== 'string') return null;
  return {
    pack: value.pack,
    title: value.title,
    summary: typeof value.summary === 'string' ? value.summary : '',
    capabilities: stringList(value.capabilities),
    modules: stringList(value.modules),
  };
}

function normalizeCatalogue(payload: unknown): CapabilityPackDefinition[] {
  if (!Array.isArray(payload)) return [];
  return payload.flatMap((row) => {
    const definition = normalizePackDefinition(row);
    return definition ? [definition] : [];
  });
}

const PACK_SOURCES = ['DEFAULT', 'ADDED', 'REMOVED', 'CONFIRMED'] as const;

function normalizeResolvedPack(value: unknown): ResolvedPack | null {
  if (!isRecord(value) || typeof value.pack !== 'string') return null;
  return {
    pack: value.pack,
    title: typeof value.title === 'string' && value.title ? value.title : value.pack,
    active: value.active === true,
    source: PACK_SOURCES.includes(value.source as (typeof PACK_SOURCES)[number])
      ? (value.source as ResolvedPack['source'])
      : 'DEFAULT',
    note: typeof value.note === 'string' && value.note ? value.note : null,
  };
}

/**
 * The resolved set is a server answer about what this institution can do; a body
 * missing packDetail is a failure, not an institution with no packs — every
 * institution has all ten packs in its detail list, active or not.
 */
function normalizeResolvedSet(payload: unknown): ResolvedCapabilitySet {
  if (!isRecord(payload) || !Array.isArray(payload.packDetail)) {
    throw new Error('The capability pack response was not in the expected shape.');
  }
  return {
    institutionId: typeof payload.institutionId === 'string' ? payload.institutionId : '',
    institutionType: typeof payload.institutionType === 'string' ? payload.institutionType : null,
    educationSubsystem:
      typeof payload.educationSubsystem === 'string' ? payload.educationSubsystem : null,
    packs: stringList(payload.packs),
    packDetail: payload.packDetail.flatMap((row) => {
      const pack = normalizeResolvedPack(row);
      return pack ? [pack] : [];
    }),
    capabilities: stringList(payload.capabilities),
    modules: stringList(payload.modules),
    warnings: stringList(payload.warnings),
  };
}

/* ─── The API surface ─── */

export const institutionExperienceApi = {
  terminology: {
    /**
     * The resolved vocabulary — every concept answered, each carrying the layer that
     * supplied it. `locale` is what the editor's EN/FR toggle sends; the response's
     * own `locale` field is the locale actually served after normalisation.
     */
    resolve: (institutionId: string, params: { locale?: string; campusId?: string } = {}) => {
      const search = new URLSearchParams();
      if (params.locale) search.set('locale', params.locale);
      if (params.campusId) search.set('campusId', params.campusId);
      const qs = search.toString();
      return core<unknown>(
        `/institutions/${seg(institutionId)}/experience/terminology${qs ? `?${qs}` : ''}`,
      ).then(normalizeResolvedTerminology);
    },

    /**
     * The stored override rows, across locales. This — not the resolved view — is
     * what knows whether THIS institution wrote a label, which is what decides
     * whether "revert to inherited" is offered.
     */
    overrides: (institutionId: string) =>
      core<unknown>(`/institutions/${seg(institutionId)}/experience/terminology/overrides`).then(
        normalizeOverrides,
      ),

    /** Upsert one label at the institution's layer (or a campus's, when campusId is in the body). */
    put: (institutionId: string, canonicalKey: CanonicalConcept, body: WriteTerminologyLabel) =>
      core<unknown>(
        `/institutions/${seg(institutionId)}/experience/terminology/${seg(canonicalKey)}`,
        { method: 'PUT', body: JSON.stringify(body) },
      ).then((payload) => normalizeOverride(payload)),

    /**
     * Clears the institution's own label for one concept and locale, letting the
     * inherited layer show through again. 404 here means "there was nothing to
     * remove", which the caller treats as already reverted.
     */
    remove: (
      institutionId: string,
      canonicalKey: CanonicalConcept,
      params: { locale: string; campusId?: string },
    ) => {
      const search = new URLSearchParams({ locale: params.locale });
      if (params.campusId) search.set('campusId', params.campusId);
      return core<void>(
        `/institutions/${seg(institutionId)}/experience/terminology/${seg(canonicalKey)}?${search}`,
        { method: 'DELETE' },
      );
    },
  },

  capabilityPacks: {
    /** The platform catalogue: every pack, its summary, and what it carries. */
    catalogue: () => core<unknown>('/experience/capability-packs').then(normalizeCatalogue),

    /** This institution's resolved set — all ten packs with active state and source. */
    resolved: (institutionId: string) =>
      core<unknown>(`/institutions/${seg(institutionId)}/experience/capability-packs`).then(
        normalizeResolvedSet,
      ),

    /**
     * Records the institution's decision for one pack. The server answers with the
     * FULL recomputed set, so the caller can replace its state without a second
     * round trip — and the card grid never shows a stale union.
     */
    set: (institutionId: string, pack: string, body: { enabled: boolean; note?: string }) =>
      core<unknown>(`/institutions/${seg(institutionId)}/experience/capability-packs/${seg(pack)}`, {
        method: 'PUT',
        body: JSON.stringify(body.note ? body : { enabled: body.enabled }),
      }).then(normalizeResolvedSet),

    /**
     * Clears the institution's decision, returning the pack to whatever the type
     * default says. Also answers with the full recomputed set. No body: the DELETE
     * route takes none.
     */
    clear: (institutionId: string, pack: string) =>
      core<unknown>(`/institutions/${seg(institutionId)}/experience/capability-packs/${seg(pack)}`, {
        method: 'DELETE',
      }).then(normalizeResolvedSet),
  },
};
